import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { readRoomCode } from "@/lib/groupSession";
import { Users, UserPlus } from "lucide-react";

const FriendNotificationListener = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleFriendshipChange = useCallback(async (payload: any) => {
    if (!user) return;
    
    const record = payload.new;
    if (!record) return;
    
    // Someone sent us a friend request
    if (payload.eventType === "INSERT" && record.friend_id === user.id && record.status === "pending") {
      // Fetch sender's username
      const { data: profiles } = await supabase.rpc("get_public_profiles", { _user_ids: [record.user_id] });
      const profile = profiles?.[0];
      
      const senderName = profile?.username || "Someone";
      
      toast(`@${senderName} sent you a friend request!`, {
        icon: <UserPlus className="w-4 h-4 text-primary" />,
        action: {
          label: "View",
          onClick: () => navigate("/friends"),
        },
        duration: 6000,
      });
    }
    
    // Someone accepted our friend request
    if (payload.eventType === "UPDATE" && record.user_id === user.id && record.status === "accepted") {
      const { data: profiles } = await supabase.rpc("get_public_profiles", { _user_ids: [record.friend_id] });
      const profile = profiles?.[0];
      
      const friendName = profile?.username || "Someone";
      toast.success(`@${friendName} accepted your friend request!`);
    }
  }, [user, navigate]);

  const handleGroupInvite = useCallback(async (payload: any) => {
    if (!user) return;
    
    const record = payload.new;
    if (!record) return;
    
    // We received a group invite
    if (payload.eventType === "INSERT" && record.to_user_id === user.id && record.status === "pending") {
      const { data: profile } = await (supabase.from("profiles") as any)
        .select("username")
        .eq("id", record.from_user_id)
        .single();
      
      const inviterName = profile?.username || "Someone";
      
      // Fetch room code via edge function (RLS restricts direct reads)
      let roomCode: string | undefined;
      try {
        roomCode = await readRoomCode(record.session_id);
      } catch {}
      
      toast(`@${inviterName} invited you to a group session!`, {
        icon: <Users className="w-4 h-4 text-primary" />,
        action: {
          label: "Join",
          onClick: async () => {
            // Mark invite as accepted
            await (supabase.from("group_invites") as any)
              .update({ status: "accepted" })
              .eq("id", record.id);
            // Navigate to discover with the room code
            navigate("/discover", { 
              state: { 
                joinGroupCode: roomCode 
              } 
            });
          },
        },
        cancel: {
          label: "Decline",
          onClick: async () => {
            await (supabase.from("group_invites") as any)
              .update({ status: "declined" })
              .eq("id", record.id);
          },
        },
        duration: 15000,
      });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;

    const friendChannel = supabase
      .channel("friend-notifications")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "friendships",
      }, handleFriendshipChange)
      .subscribe();

    const inviteChannel = supabase
      .channel("group-invite-notifications")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "group_invites",
      }, handleGroupInvite)
      .subscribe();

    return () => {
      supabase.removeChannel(friendChannel);
      supabase.removeChannel(inviteChannel);
    };
  }, [user, handleFriendshipChange, handleGroupInvite]);

  return null;
};

export default FriendNotificationListener;
